import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { ROUTE_CLIENT_ORGANIZATIONS_VIEW } from '../../../config/routes';
import { formatQuantity } from '../reportFormatters';

function CountBreakdown({ kind, value }) {
  const intl = useIntl();
  const counts = value || {};
  const categories = kind === 'signatures'
    ? ['simple', 'qualified']
    : ['simple', 'qualified', 'mixed'];

  return (
    <div className="report-activity__count">
      <strong>{formatQuantity(counts.total, intl.locale)}</strong>
      <span>
        {categories.map((category, index) => (
          <React.Fragment key={category}>
            {index > 0 && ' · '}
            <FormattedMessage id={`components.pages.reports.activity.${category}`}/>: {formatQuantity(counts[category], intl.locale)}
          </React.Fragment>
        ))}
      </span>
    </div>
  );
}

function Totals({ totals }) {
  const intl = useIntl();
  const value = totals || {};

  return (
    <section
      aria-label={intl.formatMessage({ id: 'components.pages.reports.activity.entirePeriod' })}
      className="report-activity__totals"
    >
      <article>
        <span><FormattedMessage id="components.pages.reports.activity.signaturesUsed"/></span>
        <CountBreakdown kind="signatures" value={value.signatures_used}/>
      </article>
      <article>
        <span><FormattedMessage id="components.pages.reports.activity.packagesSigned"/></span>
        <CountBreakdown kind="packages" value={value.packages_signed}/>
      </article>
    </section>
  );
}

function ActivitySeries({ series }) {
  const intl = useIntl();
  const labels = {
    month: intl.formatMessage({ id: 'components.pages.reports.activity.month' }),
    packages: intl.formatMessage({ id: 'components.pages.reports.activity.packagesSigned' }),
    signatures: intl.formatMessage({ id: 'components.pages.reports.activity.signaturesUsed' })
  };

  return (
    <div className="report-activity__table-wrap">
      <table className="report-activity__table">
        <caption className="sr-only"><FormattedMessage id="components.pages.reports.activity.monthlyDistribution"/></caption>
        <thead>
          <tr>
            <th scope="col">{labels.month}</th>
            <th scope="col">{labels.signatures}</th>
            <th scope="col">{labels.packages}</th>
          </tr>
        </thead>
        <tbody>
          {(series || []).map(bucket => (
            <tr data-testid={`activity-period-${bucket.key}`} key={bucket.key}>
              <td data-label={labels.month}>
                <strong>{bucket.label || bucket.key || '—'}</strong>
                <span>{bucket.date_from || '—'} – {bucket.date_to || '—'}</span>
              </td>
              <td data-label={labels.signatures}>
                <CountBreakdown kind="signatures" value={bucket.signatures_used}/>
              </td>
              <td data-label={labels.packages}>
                <CountBreakdown kind="packages" value={bucket.packages_signed}/>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityPagination({ meta, onTableChange, table }) {
  const intl = useIntl();
  const pagination = (meta && meta.pagination) || {};
  const page = Number(pagination.page || table.page);
  const lastPage = Math.max(1, Number(pagination.last_page || 1));
  const total = Number(pagination.total || 0);
  const perPage = Number(pagination.per_page || table.perPage);
  const first = total === 0 ? 0 : ((page - 1) * perPage) + 1;
  const last = Math.min(total, page * perPage);

  return (
    <footer className="report-activity__footer">
      <label>
        <FormattedMessage id="components.pages.reports.activity.organizationsPerPage"/>
        <select onChange={event => onTableChange({ page: 1, perPage: Number(event.target.value) })} value={perPage}>
          {[10, 25, 50, 100].map(value => <option key={value} value={value}>{value}</option>)}
        </select>
      </label>
      <span aria-live="polite">
        <FormattedMessage id="components.pages.reports.activity.range" values={{ first, last, total }}/>
      </span>
      <nav aria-label={intl.formatMessage({ id: 'components.pages.reports.activity.pagination' })}>
        <button aria-label={intl.formatMessage({ id: 'components.pages.reports.activity.firstPage' })} disabled={page <= 1} onClick={() => onTableChange({ ...table, page: 1 })} type="button"><i aria-hidden="true" className="fas fa-angle-double-left"/></button>
        <button aria-label={intl.formatMessage({ id: 'components.pages.reports.activity.previousPage' })} disabled={page <= 1} onClick={() => onTableChange({ ...table, page: page - 1 })} type="button"><i aria-hidden="true" className="fas fa-angle-left"/></button>
        <span>{page} / {lastPage}</span>
        <button aria-label={intl.formatMessage({ id: 'components.pages.reports.activity.nextPage' })} disabled={page >= lastPage} onClick={() => onTableChange({ ...table, page: page + 1 })} type="button"><i aria-hidden="true" className="fas fa-angle-right"/></button>
        <button aria-label={intl.formatMessage({ id: 'components.pages.reports.activity.lastPage' })} disabled={page >= lastPage} onClick={() => onTableChange({ ...table, page: lastPage })} type="button"><i aria-hidden="true" className="fas fa-angle-double-right"/></button>
      </nav>
    </footer>
  );
}

function ActivityReport({ data, error, loading, onRetry, onTableChange, scoped, table }) {
  const intl = useIntl();
  if (loading) {
    return <div aria-live="polite" className="report-products-loading" role="status"><span/><span/><span/><span className="sr-only"><FormattedMessage id="components.pages.reports.activity.loading"/></span></div>;
  }
  if (error) {
    return <div className="report-state report-state--error" role="alert"><FormattedMessage id="components.pages.reports.activity.error"/><button className="btn btn-outline-primary" onClick={onRetry} type="button"><FormattedMessage id="components.pages.reports.activity.retry"/></button></div>;
  }
  if (!data || (!scoped && (!Array.isArray(data.rows) || data.rows.length === 0)) || (scoped && !Array.isArray(data.series))) {
    return <div className="report-state"><FormattedMessage id="components.pages.reports.activity.empty"/></div>;
  }

  const rows = scoped ? [{ uuid: 'scoped', totals: data.totals, series: data.series }] : data.rows;
  return (
    <section aria-label={intl.formatMessage({ id: 'components.pages.reports.activity.title' })} className="report-activity">
      <header><h2><FormattedMessage id="components.pages.reports.activity.title"/></h2><p><FormattedMessage id="components.pages.reports.activity.hint"/></p></header>
      {data.totals && <Totals totals={data.totals}/>}
      {rows.map(row => (
        <article className="report-activity__organization" key={row.uuid}>
          {!scoped && (
            <h3>
              <Link to={`${ROUTE_CLIENT_ORGANIZATIONS_VIEW.replace(':organizationId', row.uuid)}?tab=reports&report_tab=activity`}>
                {row.name || row.uuid}
              </Link>
            </h3>
          )}
          {!scoped && <Totals totals={row.totals}/>}
          <ActivitySeries series={row.series}/>
        </article>
      ))}
      {!scoped && data.meta && data.meta.pagination && <ActivityPagination meta={data.meta} onTableChange={onTableChange} table={table}/>}
    </section>
  );
}

export { ActivityReport, ActivitySeries, CountBreakdown, Totals };
